import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import taskListService from '../../service/taskList.service';
import useMenus from '../../context/MenuContext';
import SimpleTaskListTabs from './SimpleTaskListTabs';

export const SimpleTaskListPage = ({ open = false, data = {}, handleClose = () => {}, setTaskChanged = () => {} }) => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [submenu, setSubmenu] = useState(null);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    user: [],
    userGroup: [],
    type: [],
    status: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [viewField, setViewField] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const def = {
    startDate: dayjs(),
    lineId: null,
    name: '',
    duration: 1,
    responsiblePersonId: null,
    userGroupId: null,
    typeId: null,
    statusId: null,
    description: '',
    attachment: [],
    taskListRespPerson: [],
  };

  useEffect(() => {
    setInitValues(def);
  }, []);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    setInitValues(def);
    getMenus();
  }, []);

  useEffect(() => {
    if (Object.keys(data).length > 0 && autocompleteOptions.project.length > 0) {
      const formatted = {
        ...data,
        startDate: dayjs(data.startDate),
        attachment: data.taskListAttachment ?? [],
      };

      setInitValues(formatted);
      setViewField(true);
    }
  }, [data, autocompleteOptions.project]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('simpleTask');
    setSubmenu(submenuId);
  }, [menus]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setInitValues((prev) => ({
        ...prev,
        typeId: autocompleteData?.type?.defaultValue ?? null,
        statusId: autocompleteData?.status?.defaultValue ?? null,
      }));
    }
  }, [autocompleteData]);

  const validationSchema = Yup.object().shape({
    lineId: Yup.string().required(),
    startDate: Yup.string().required(),
    name: Yup.string().required(),
    duration: Yup.number().required(),
    responsiblePersonId: Yup.string().nullable(),
    userGroupId: Yup.string().nullable(),
    typeId: Yup.string().required(),
    statusId: Yup.string().required(),
    description: Yup.string().nullable().max(1500),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          user: resp[1],
          userGroup: resp[2],
          type: resp[3],
          status: resp[4],
        };
        setAutocompleteData(aData);
        const acOptions = {
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroup: generateAutocompleteItems(aData.userGroup, 'name', 'id'),
          type: generateAutocompleteItems(aData.type.typeItems, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();
    const insertObject = {
      ...values,
      createdBy: user.userId,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
      duration: parseInt(values.duration),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.attachment) {
      for (const file of insertObject.attachment) {
        formData.append('files', file);
      }
    }

    taskListService
      .createTaskList(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
        setTaskChanged(true);
        handleClose();
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    taskListService.deleteTaskList(deleteData, data.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setTaskChanged(true);
      handleClose();
    });
  };

  return (
    <div className={`${!open && 'pt-20 fixed top-0 left-0 overflow-auto'} bg-gray-100 h-full w-full`}>
      <div className={`${!open && 'py-5 px-5 md:w-[650px] w-[400px]  mx-auto'} h-auto bg-white rounded-lg`}>
        {Object.keys(data).length > 0 && (
          <FormHeaderUI
            modClick={() => {
              setViewField(false);
            }}
            modDisabled={!viewField}
            deleteClick={() => {
              setShowDeleteDialog(true);
            }}
            deleteDisabled={!viewField}
          />
        )}
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleInsertObject}
          enableReinitialize
        >
          {() => (
            <Form>
              <FormFields autocompleteOptions={autocompleteOptions} user={user} viewField={viewField} />
              <SimpleTaskListTabs />

              <FormButtonPanelUI />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
